import { ChatKit, Entity, type UseChatKitOptions, useChatKit } from "@openai/chatkit-react";
import { Select } from "antd";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useRootPath } from "../../hooks/app.hook";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
} from "../../slices/appStateSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import { eventEmitter } from "../../utils/eventEmitter";
import styles from "./Superdoc.module.scss";

// Module-level store for noteFirstRead events that fire while Superdoc is
// unmounted or re-initialising (e.g. during the async thread fetch after a
// patient change). SuperdocChatKit picks this up on mount.
let pendingNoteFirstRead: { id: number; client?: number } | null = null;
eventEmitter.on("noteFirstRead", (...args: unknown[]) => {
  const data = args[0] as { id?: number; client?: number };
  if (data?.id) {
    pendingNoteFirstRead = { id: data.id, client: data.client };
  }
});

interface SuperdocComponentProps {
  clientId?: number;
  modelId?: string;
  className?: string;
  style?: React.CSSProperties;
  onExpand?: () => void;
  onCollapse?: () => void;
  expanded?: boolean;
}

interface SuperdocChatKitProps extends SuperdocComponentProps {
  initialThreadId: string | null;
}

const PersonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8 7.5C9.38071 7.5 10.5 6.38071 10.5 5C10.5 3.61929 9.38071 2.5 8 2.5C6.61929 2.5 5.5 3.61929 5.5 5C5.5 6.38071 6.61929 7.5 8 7.5ZM8 8.5C4.5 8.5 2.5 10.5 2.5 12.5C2.5 13.0523 2.94772 13.5 3.5 13.5H12.5C13.0523 13.5 13.5 13.0523 13.5 12.5C13.5 10.5 11.5 8.5 8 8.5Z"
      fill="#999CA0"
    />
  </svg>
);

const SuperdocChatKit: React.FC<SuperdocChatKitProps> = ({
  clientId,
  modelId,
  className,
  style,
  onExpand,
  onCollapse,
  expanded = false,
  initialThreadId,
}) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobileView();
  const { rootPath } = useRootPath();

  // Stable refs to avoid circular dependency between superdocOptions and useChatKit.
  const sendMessageRef = useRef<((params: { text: string }) => Promise<void>) | null>(null);
  const setThreadIdRef = useRef<((threadId: string | null) => Promise<void>) | null>(null);

  // Queue custom actions when superdoc is busy streaming a response.
  const isRespondingRef = useRef(false);
  const actionQueueRef = useRef<Array<{ type: string; payload?: Record<string, unknown> }>>([]);

  const { clients, sharedClients } = useAppSelector((state) => state.client);
  const allClients = useMemo(() => [...clients, ...sharedClients], [clients, sharedClients]);

  const clientOptions = useMemo(() => allClients.map((c) => ({ value: c.id, label: c.name })), [allClients]);

  const handleSelectClient = useCallback(
    (value: number | undefined) => {
      if (!value) {
        dispatch(setSelectedClient(null));
        navigate(rootPath);
        return;
      }
      const client = allClients.find((c) => c.id === value) || null;
      if (client) {
        dispatch(setLoadingNotesState(true));
        dispatch(setSelectedClient(client));
        dispatch(setNotesViewVisible(false));
        dispatch(setNotesDetails(null));
        dispatch(setCustomiseSectionState(false));
        dispatch(resetNotesDetails());
        navigate(`${rootPath}/clients/${client.uuid}`);
      }
    },
    [allClients, dispatch, navigate, rootPath],
  );

  const handleTagSearch = useCallback(
    async (query: string): Promise<Entity[]> => {
      const lowerQuery = query.toLowerCase();
      return allClients
        .filter((client) => client.name.toLowerCase().includes(lowerQuery))
        .slice(0, 10)
        .map((client) => ({
          title: client.name,
          id: String(client.id),
          icon: "profile" as const,
          interactive: true,
          group: "Clients",
          data: { clientId: String(client.id) },
        }));
    },
    [allClients],
  );

  const toggleExpand = useCallback(() => {
    if (expanded) {
      onCollapse?.();
    } else {
      onExpand?.();
    }
  }, [expanded, onExpand, onCollapse]);

  const superdocRefLocal = useRef<import("@openai/chatkit").OpenAIChatKit | null>(null);

  const processActionQueue = useCallback(async () => {
    const ref = superdocRefLocal.current;
    if (!ref) return;
    while (actionQueueRef.current.length > 0) {
      const action = actionQueueRef.current.shift()!;
      try {
        await ref.sendCustomAction(action);
      } catch {
        // Silently fail
      }
    }
  }, []);

  const handleResponseStart = useCallback(() => {
    isRespondingRef.current = true;
  }, []);

  const handleResponseEnd = useCallback(() => {
    isRespondingRef.current = false;
    processActionQueue();
  }, [processActionQueue]);

  const sendOrQueueCustomAction = useCallback(async (action: { type: string; payload?: Record<string, unknown> }) => {
    if (!superdocRefLocal.current || isRespondingRef.current) {
      actionQueueRef.current.push(action);
      return;
    }
    try {
      await superdocRefLocal.current.sendCustomAction(action);
    } catch {
      // Silently fail
    }
  }, []);

  const customFetch: typeof fetch = useCallback(
    async (input, init) => {
      const token = localStorage.getItem("token");
      const headers = new Headers(init?.headers);

      headers.set("Content-Type", "application/json");

      if (token) {
        headers.set("Authorization", `Token ${token}`);
      }

      if (clientId) {
        headers.set("X-Client-ID", clientId.toString());
      }

      if (modelId) {
        headers.set("X-Model", modelId);
      }

      return fetch(input, {
        ...init,
        headers,
      });
    },
    [modelId, clientId],
  );

  const apiConfig = useMemo(
    () => ({
      url: `${baseUrl}/superdoc/`,
      domainKey:
        process.env.REACT_APP_OPENAI_DOMAIN_KEY || "domain_pk_697a58d598288194860d3400dee1f1c00d82763431390419",
      fetch: customFetch,
      uploadStrategy: { type: "two_phase" as const },
    }),
    [baseUrl, customFetch],
  );

  const superdocOptions: UseChatKitOptions = useMemo(
    () => ({
      api: apiConfig,
      initialThread: initialThreadId,
      theme: {
        colorScheme: "light",
        color: {
          accent: {
            primary: "#6366F1",
            level: 2,
          },
          surface: {
            background: "#f5f5f5",
            foreground: "#fff",
          },
        },
        typography: {
          baseSize: 14,
        },
        radius: "round",
        density: "normal",
      },
      header: {
        enabled: true,
        title: {
          enabled: false,
          text: "Superdoc",
        },
        rightAction: isMobile
          ? undefined
          : {
              icon: expanded ? "collapse-large" : "expand-large",
              onClick: toggleExpand,
            },
      },
      composer: {
        placeholder: "Ask Superdoc anything...",
        attachments: {
          enabled: false,
        },
      },
      disclaimer: {
        text: `Superdoc is experimental. Verify everything.`,
      },
      startScreen: {
        greeting: "Hi, I'm Superdoc! How can I help you today?",
        prompts: [
          {
            label: "Summarize today's notes",
            prompt: "Can you summarize the notes I created today?",
            icon: "document",
          },
          {
            label: "Help with diagnosis",
            prompt: "Help me think through a differential diagnosis for my current patient.",
            icon: "lightbulb",
          },
          {
            label: "Treatment suggestions",
            prompt: "What treatment options should I consider for my patient?",
            icon: "sparkle",
          },
        ],
      },
      history: {
        enabled: true,
      },
      entities: {
        onTagSearch: handleTagSearch,
      },
      widgets: {
        onAction: async (action: { type: string; payload?: Record<string, unknown> }, _widgetItem: { id: string }) => {
          console.log("action", action);
          if (action.type === "send_followup") {
            const question = action.payload?.question as string;
            if (question && sendMessageRef.current) {
              await sendMessageRef.current({ text: question });
            }
          } else if (action.type === "open_url") {
            const url = action.payload?.url as string;
            if (url) {
              window.open(url, "_blank", "noopener,noreferrer");
            }
          } else if (action.type === "dismiss") {
            // No-op
          }
        },
      },
      onError: (detail: { error: Error }) => {
        console.error("[Superdoc] ChatKit error:", detail.error);
      },
      onResponseStart: handleResponseStart,
      onResponseEnd: handleResponseEnd,
    }),
    [apiConfig, initialThreadId, expanded, toggleExpand, handleTagSearch, handleResponseStart, handleResponseEnd],
  );

  const { control: superdocControl, ref: superdocRef, sendUserMessage, setThreadId } = useChatKit(superdocOptions);
  sendMessageRef.current = sendUserMessage;
  setThreadIdRef.current = setThreadId;
  superdocRefLocal.current = superdocRef.current;

  // Flush queued actions once ChatKit is fully ready.
  // The ref becomes truthy before ChatKit finishes internal init, so we
  // poll with a short delay to give it time to become truly operational.
  useEffect(() => {
    if (!superdocRef.current || actionQueueRef.current.length === 0 || isRespondingRef.current) return;

    let cancelled = false;
    const attempt = (retries: number) => {
      if (cancelled || actionQueueRef.current.length === 0) return;
      processActionQueue().then(() => {
        // If queue still has items (processActionQueue bailed), retry after delay
        if (!cancelled && actionQueueRef.current.length > 0 && retries > 0) {
          setTimeout(() => attempt(retries - 1), 500);
        }
      });
    };

    // First attempt after a short delay to let ChatKit finish initializing
    const timer = setTimeout(() => attempt(5), 500);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  });

  // Trigger post-encounter clinical review when user opens a note for the first time.
  // Because the Superdoc remounts (async thread fetch) when the patient changes,
  // the "noteFirstRead" event may fire before this component is mounted.
  // We store a pending payload so we can pick it up once we're ready.
  useEffect(() => {
    // Process any event that fired while we were unmounted / initialising
    const pending = pendingNoteFirstRead;
    if (pending) {
      pendingNoteFirstRead = null;
      sendOrQueueCustomAction({
        type: "post_encounter_clinical_review",
        payload: { audio_id: pending.id },
      });
    }

    const handleNoteFirstRead = async (...args: unknown[]) => {
      const data = args[0] as { id?: number; client?: number };
      if (data?.id) {
        await sendOrQueueCustomAction({
          type: "post_encounter_clinical_review",
          payload: { audio_id: data.id },
        });
      }
    };

    eventEmitter.on("noteFirstRead", handleNoteFirstRead);
    return () => {
      eventEmitter.off("noteFirstRead", handleNoteFirstRead);
    };
  }, [sendOrQueueCustomAction]);

  // Post EHR transfer status messages to the chat thread
  useEffect(() => {
    const sendTransferStatus = async (audioId: number, transferStatus: string) => {
      await sendOrQueueCustomAction({
        type: "ehr_transfer_status",
        payload: { audio_id: audioId, status: transferStatus },
      });
    };

    const handleStarted = (...args: unknown[]) => {
      const data = args[0] as { audioId?: number };
      if (data?.audioId) sendTransferStatus(data.audioId, "started");
    };
    const handleCompleted = (...args: unknown[]) => {
      const data = args[0] as { audioId?: number };
      if (data?.audioId) sendTransferStatus(data.audioId, "completed");
    };
    const handleFailed = (...args: unknown[]) => {
      const data = args[0] as { audioId?: number };
      if (data?.audioId) sendTransferStatus(data.audioId, "failed");
    };

    eventEmitter.on("ehrTransferStarted", handleStarted);
    eventEmitter.on("ehrTransferCompleted", handleCompleted);
    eventEmitter.on("ehrTransferFailed", handleFailed);
    return () => {
      eventEmitter.off("ehrTransferStarted", handleStarted);
      eventEmitter.off("ehrTransferCompleted", handleCompleted);
      eventEmitter.off("ehrTransferFailed", handleFailed);
    };
  }, [sendOrQueueCustomAction]);

  return (
    <div className={`${styles.superdoc__wrapper} ${className || ""}`} style={style}>
      {/* Patient selector */}
      <div className={styles["superdoc__patient-selector"]}>
        <div className={styles["superdoc__patient-selector-title"]}>
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M14.3815 0.768963C14.3374 0.551947 14.2197 0.356839 14.0482 0.216698C13.8768 0.0765565 13.6621 0 13.4407 0C13.2193 0 13.0046 0.0765565 12.8332 0.216698C12.6617 0.356839 12.544 0.551947 12.4999 0.768963L12.2695 1.91328C12.2324 2.09916 12.1412 2.26992 12.0073 2.40403C11.8733 2.53814 11.7027 2.6296 11.5169 2.66688L10.3725 2.89536C10.1545 2.93849 9.95814 3.05595 9.81703 3.2277C9.67592 3.39944 9.59878 3.61484 9.59878 3.83712C9.59878 4.05941 9.67592 4.2748 9.81703 4.44655C9.95814 4.6183 10.1545 4.73576 10.3725 4.77888L11.5169 5.00736C11.7029 5.04448 11.8737 5.13586 12.0078 5.26998C12.142 5.40411 12.2333 5.57495 12.2705 5.76096L12.4989 6.90528C12.5421 7.12334 12.6595 7.31968 12.8313 7.46079C13.003 7.6019 13.2184 7.67904 13.4407 7.67904C13.663 7.67904 13.8784 7.6019 14.0501 7.46079C14.2219 7.31968 14.3393 7.12334 14.3825 6.90528L14.6109 5.76096C14.648 5.57495 14.7394 5.40411 14.8736 5.26998C15.0077 5.13586 15.1785 5.04448 15.3645 5.00736L16.5089 4.77888C16.7269 4.73576 16.9233 4.6183 17.0644 4.44655C17.2055 4.2748 17.2826 4.05941 17.2826 3.83712C17.2826 3.61484 17.2055 3.39944 17.0644 3.2277C16.9233 3.05595 16.7269 2.93849 16.5089 2.89536L15.3645 2.66688C15.1785 2.62977 15.0077 2.53838 14.8736 2.40426C14.7394 2.27014 14.648 2.09929 14.6109 1.91328L14.3825 0.768963H14.3815ZM5.71173 4.49376C5.64816 4.3024 5.52594 4.13591 5.36241 4.01792C5.19888 3.89993 5.00235 3.83643 4.80069 3.83643C4.59904 3.83643 4.40251 3.89993 4.23898 4.01792C4.07545 4.13591 3.95323 4.3024 3.88966 4.49376L3.23397 6.46272C3.18686 6.60426 3.10741 6.73288 3.00193 6.83836C2.89645 6.94384 2.76784 7.02329 2.6263 7.0704L0.657335 7.72608C0.465968 7.78966 0.299479 7.91188 0.181489 8.07541C0.0634993 8.23894 0 8.43547 0 8.63712C0 8.83877 0.0634993 9.03531 0.181489 9.19884C0.299479 9.36237 0.465968 9.48459 0.657335 9.54816L2.6263 10.2048C2.76773 10.2518 2.89628 10.3311 3.00175 10.4364C3.10723 10.5417 3.18674 10.6702 3.23397 10.8115L3.88966 12.7805C3.95323 12.9719 4.07545 13.1383 4.23898 13.2563C4.40251 13.3743 4.59904 13.4378 4.80069 13.4378C5.00235 13.4378 5.19888 13.3743 5.36241 13.2563C5.52594 13.1383 5.64816 12.9719 5.71173 12.7805L6.36741 10.8115C6.41453 10.67 6.49398 10.5414 6.59946 10.4359C6.70494 10.3304 6.83356 10.251 6.9751 10.2038L8.94406 9.54816C9.13542 9.48459 9.30191 9.36237 9.4199 9.19884C9.53789 9.03531 9.60139 8.83877 9.60139 8.63712C9.60139 8.43547 9.53789 8.23894 9.4199 8.07541C9.30191 7.91188 9.13542 7.78966 8.94406 7.72608L6.9751 7.0704C6.83356 7.02329 6.70494 6.94384 6.59946 6.83836C6.49398 6.73288 6.41453 6.60426 6.36741 6.46272L5.71173 4.49376ZM12.4317 12.1738C12.3682 11.9824 12.2459 11.8159 12.0824 11.6979C11.9189 11.5799 11.7223 11.5164 11.5207 11.5164C11.319 11.5164 11.1225 11.5799 10.959 11.6979C10.7955 11.8159 10.6732 11.9824 10.6097 12.1738L10.433 12.7027C10.386 12.8442 10.3067 12.9727 10.2014 13.0782C10.0961 13.1837 9.96766 13.2632 9.82629 13.3104L9.29734 13.4861C9.10597 13.5497 8.93948 13.6719 8.82149 13.8354C8.7035 13.9989 8.64 14.1955 8.64 14.3971C8.64 14.5988 8.7035 14.7953 8.82149 14.9588C8.93948 15.1224 9.10597 15.2446 9.29734 15.3082L9.82629 15.4838C9.96784 15.531 10.0964 15.6104 10.2019 15.7159C10.3074 15.8214 10.3869 15.95 10.434 16.0915L10.6097 16.6205C10.6732 16.8119 10.7955 16.9783 10.959 17.0963C11.1225 17.2143 11.319 17.2778 11.5207 17.2778C11.7223 17.2778 11.9189 17.2143 12.0824 17.0963C12.2459 16.9783 12.3682 16.8119 12.4317 16.6205L12.6084 16.0915C12.6554 15.9501 12.7347 15.8215 12.84 15.7161C12.9453 15.6106 13.0737 15.5311 13.2151 15.4838L13.7441 15.3082C13.9354 15.2446 14.1019 15.1224 14.2199 14.9588C14.3379 14.7953 14.4014 14.5988 14.4014 14.3971C14.4014 14.1955 14.3379 13.9989 14.2199 13.8354C14.1019 13.6719 13.9354 13.5497 13.7441 13.4861L13.2151 13.3094C13.0737 13.2624 12.9451 13.1831 12.8396 13.0778C12.7342 12.9725 12.6547 12.8441 12.6074 12.7027L12.4317 12.1738Z"
              fill="#6366F1"
            />
          </svg>
          <span>Superdoc</span>
        </div>
        <div className={styles["superdoc__patient-selector-content"]}>
          <span className={styles["superdoc__patient-icon"]}>
            <PersonIcon />
          </span>
          <span className={styles["superdoc__patient-label"]}>Patient:</span>
          <Select
            showSearch
            allowClear
            value={clientId}
            placeholder="Select a patient to add context"
            onChange={handleSelectClient}
            onClear={() => handleSelectClient(undefined)}
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ?? false
            }
            options={clientOptions}
            variant="borderless"
            className={styles["superdoc__patient-select"]}
          />
        </div>
      </div>

      <div className={styles.superdoc__content}>
        <ChatKit control={superdocControl} />
      </div>
    </div>
  );
};

/**
 * Outer wrapper that fetches the latest thread ID before mounting ChatKit.
 * This ensures `initialThread` is set correctly so ChatKit loads existing
 * conversation history instead of showing the empty start screen.
 *
 * The component remounts when `clientId` changes (via `key` prop in home.page.tsx),
 * so this fetch runs fresh for each patient.
 */
const SuperdocComponent: React.FC<SuperdocComponentProps> = (props) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  const [initialThreadId, setInitialThreadId] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    const fetchLatestThread = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setInitialThreadId(null);
          return;
        }
        const url = new URL(`${baseUrl}/superdoc/thread/latest/`);
        if (props.clientId) url.searchParams.set("client_id", props.clientId.toString());

        const res = await fetch(url.toString(), {
          headers: { Authorization: `Token ${token}` },
        });
        if (!res.ok) {
          setInitialThreadId(null);
          return;
        }
        const data = await res.json();
        setInitialThreadId(data.thread_id || null);
      } catch (err) {
        console.error("[Superdoc] Failed to fetch latest thread:", err);
        setInitialThreadId(null);
      }
    };
    fetchLatestThread();
  }, [baseUrl, props.clientId]);

  // Show nothing while fetching — prevents ChatKit from mounting with initialThread=null
  // and then having to switch threads after mount
  if (initialThreadId === undefined) {
    return (
      <div className={`${styles.superdoc__wrapper} ${props.className || ""}`} style={props.style}>
        <div className={styles.superdoc__content} />
      </div>
    );
  }

  return <SuperdocChatKit {...props} initialThreadId={initialThreadId} />;
};

export default SuperdocComponent;
